/**
 * UmsatzChart — the Umsatz- & Bestandsentwicklung card (Story UI-U3, AC 2).
 * Ported LAYOUT from the reference prototype's umsatz-chart.tsx onto our as-built
 * recharts usage — the prototype's shadcn ChartContainer is NOT ported.
 *
 * Series assigned strictly in chart-1→chart-2 token order (design.md §2):
 *   Umsatz / Prämienvolumen → chart-1 (area, left axis)
 *   Bestand / Policenanzahl → chart-2 (line, right axis)
 * Colours come ONLY from CSS vars; range switch is UI-U1's SegmentedControl.
 *
 * Empty (live mode — no endpoint): a design-conform empty state.
 *
 * [Source: brokly-design-reference/components/brokly/umsatz-chart.tsx]
 * [Source: apps/web/src/features/dashboard/DashboardScreen.tsx (as-built recharts pattern)]
 */
import { useState } from 'react';
import { Area, ComposedChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '../../../components/ui/Card';
import { SegmentedControl } from '../../../components/ui/SegmentedControl';
import { chf } from '../../../lib/format';
import type { HeuteChartPoint } from '../heute-mock';

const RANGES = [
  { value: '3m', label: '3 Monate' },
  { value: '30d', label: '30 Tage' },
  { value: '7d', label: '7 Tage' },
];

export function UmsatzChart({ series }: { series: HeuteChartPoint[] }) {
  const [range, setRange] = useState('3m');

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">Umsatz- &amp; Bestandsentwicklung</div>
          <div className="text-xs text-muted-foreground">Prämienvolumen und Policenbestand im Verlauf</div>
        </div>
        <SegmentedControl
          ariaLabel="Zeitraum"
          value={range}
          onValueChange={setRange}
          options={RANGES}
        />
      </div>
      {series.length === 0 ? (
        <div className="grid h-64 place-items-center text-sm text-muted-foreground">
          Noch keine Umsatzdaten.
        </div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="umsatz-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="var(--border)" />
              <XAxis dataKey="m" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
              <YAxis
                yAxisId="umsatz"
                tickLine={false}
                axisLine={false}
                width={48}
                tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                tickFormatter={(v: number) => `${Math.round(v / 1000)}k`}
              />
              <YAxis
                yAxisId="bestand"
                orientation="right"
                tickLine={false}
                axisLine={false}
                width={32}
                tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--popover)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 12,
                  color: 'var(--popover-foreground)',
                }}
                formatter={(value, name) =>
                  name === 'Prämienvolumen' ? [chf(Number(value)), name] : [String(value), 'Policen']
                }
              />
              <Area
                yAxisId="umsatz"
                type="monotone"
                dataKey="v"
                name="Prämienvolumen"
                stroke="var(--chart-1)"
                strokeWidth={2}
                fill="url(#umsatz-fill)"
              />
              <Line
                yAxisId="bestand"
                type="monotone"
                dataKey="b"
                name="Policen"
                stroke="var(--chart-2)"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
